import { useState } from 'preact/hooks';

// The larger shared pieces: preview before saving. (The Filters button and panel are the app's
// own, in shelves/Filters.tsx; the Pieces page shows those.)

export interface PreviewRow {
  id: string;
  /** What was found, as it will be saved. */
  text: string;
  /** Where it goes ("Quote · Loc 1,204"). */
  detail?: string;
  /** Already saved, or left out last time: folded and not offered again. */
  already?: boolean;
  /** Something to check (shown with the row). */
  flag?: string;
}

/**
 * Preview before saving (decisions doc, Oct 21): what an import or paste found, a tick per
 * row, the already-there entries folded into one line, then one Save for the whole batch
 * (with Undo afterwards).
 */
export function PreviewList({
  found,
  rows,
  saveLabel,
  onSave,
  onCancel,
}: {
  found: string;
  rows: PreviewRow[];
  saveLabel: (n: number) => string;
  onSave: (ids: string[]) => void;
  onCancel: () => void;
}) {
  const fresh = rows.filter((r) => !r.already);
  const old = rows.filter((r) => r.already);
  const [ticked, setTicked] = useState(() => new Set(fresh.map((r) => r.id)));
  const [showOld, setShowOld] = useState(false);
  const toggle = (id: string) => {
    const next = new Set(ticked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setTicked(next);
  };

  return (
    <div class="preview" role="group" aria-label="Preview before saving">
      <p class="preview-found">{found}</p>
      <div class="preview-tools">
        <button type="button" class="btn-link" onClick={() => setTicked(new Set(fresh.map((r) => r.id)))}>
          Tick all
        </button>
        <button type="button" class="btn-link" onClick={() => setTicked(new Set())}>
          Untick all
        </button>
      </div>
      <ul class="preview-rows">
        {fresh.map((r) => (
          <li key={r.id} class="preview-row">
            <label>
              <input type="checkbox" checked={ticked.has(r.id)} onChange={() => toggle(r.id)} />
              <span class="preview-text">{r.text}</span>
            </label>
            {r.detail ? <span class="preview-detail">{r.detail}</span> : null}
            {r.flag ? <span class="preview-flag">{r.flag}</span> : null}
          </li>
        ))}
      </ul>
      {old.length ? (
        <div class="preview-old">
          <button type="button" class="btn-link" aria-expanded={showOld} onClick={() => setShowOld(!showOld)}>
            {old.length} already saved or left out · {showOld ? 'Hide' : 'Show'}
          </button>
          {showOld ? (
            <ul class="preview-rows quiet">
              {old.map((r) => (
                <li key={r.id} class="preview-row">
                  <span class="preview-text">{r.text}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <div class="preview-actions">
        <button type="button" class="btn-main" disabled={ticked.size === 0} onClick={() => onSave([...ticked])}>
          {saveLabel(ticked.size)}
        </button>
        <button type="button" class="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
